import { Analytics } from "@vercel/analytics/react"
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { SessionCommsProvider, useSessionComms } from './contexts/SessionCommsContext';
import LandingPage from './pages/LandingPage';
import Settings from './pages/Settings';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ImposterSetup from './pages/imposter/ImposterSetup';
import ImposterReveal from './pages/imposter/ImposterReveal';
import ImposterVoting from './pages/imposter/ImposterVoting';
import VirtualImposter from './pages/imposter/VirtualImposter';
import UnoTest from './pages/uno/UnoTest';
import UnoLogicTest from './pages/uno/UnoLogicTest';
import UnoSetup from './pages/uno/UnoSetup';
import UnoGame from './pages/uno/UnoGame';
import UnoNMSetup from './pages/uno-nm/UnoNMSetup';
import UnoNMGame from './pages/uno-nm/UnoNMGame';
import MafiaSetup from './pages/mafia/MafiaSetup';
import MafiaGame from './pages/mafia/MafiaGame';
import MafiaReveal from './pages/mafia/MafiaReveal';
import MafiaResults from './pages/mafia/MafiaResults';
import MafiaNarrator from './pages/mafia/MafiaNarrator';
import GuestSessionPage from './pages/social/GuestSessionPage';
import CharadesSetup from './pages/charades/CharadesSetup';
import CharadesGame from './pages/charades/CharadesGame';
import TrioSetup from './pages/trio/TrioSetup';
import TrioGame from './pages/trio/TrioGame';
import AccountGameStateSync from './components/AccountGameStateSync';
import { useTheme } from './contexts/ThemeContext';
import { sessionApi } from './services/sessionApi';
import storageManager from './utils/StorageManager';

const HOST_SESSION_CACHE_KEY = 'guest_host_active_session';
const SESSION_FORCED_GAME_ROUTES = new Set(['/trio-game', '/uno-game', '/uno-nm-game']);
const MOBILE_HUD_BREAKPOINT = 768;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function readHudState(key) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeHudState(key, payload) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // no-op
  }
}

function useMobileHudPanel({
  storageKey,
  desktopPosition,
  mobileDefaultPosition,
  panelWidth = 320,
  panelHeight = 96,
  defaultCollapsed = false,
}) {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_HUD_BREAKPOINT : false
  ));
  const [collapsed, setCollapsed] = useState(() => {
    const stored = readHudState(storageKey);
    if (typeof stored?.collapsed === 'boolean') return stored.collapsed;
    return defaultCollapsed;
  });
  const [position, setPosition] = useState(() => {
    const fallback = mobileDefaultPosition();
    const stored = readHudState(storageKey);
    if (typeof stored?.x === 'number' && typeof stored?.y === 'number') {
      return { x: stored.x, y: stored.y };
    }
    return fallback;
  });
  const panelRef = useRef(null);
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const getPanelSize = () => {
    const panelRect = panelRef.current?.getBoundingClientRect?.();
    return {
      width: Math.max(220, Math.ceil(panelRect?.width || panelWidth)),
      height: Math.max(44, Math.ceil(panelRect?.height || panelHeight)),
    };
  };

  const clampPosition = (nextX, nextY) => {
    if (typeof window === 'undefined') return { x: nextX, y: nextY };
    const { width, height } = getPanelSize();
    const maxX = Math.max(8, window.innerWidth - width - 8);
    const maxY = Math.max(8, window.innerHeight - height - 8);
    return {
      x: clamp(nextX, 8, maxX),
      y: clamp(nextY, 8, maxY),
    };
  };

  const persistState = (next) => {
    writeHudState(storageKey, {
      x: next.x,
      y: next.y,
      collapsed,
    });
  };

  const onDragStart = (event) => {
    if (!isMobile) return;
    event.preventDefault();
    const pointerX = event.clientX ?? event.touches?.[0]?.clientX ?? 0;
    const pointerY = event.clientY ?? event.touches?.[0]?.clientY ?? 0;
    dragStateRef.current = {
      active: true,
      startX: pointerX,
      startY: pointerY,
      originX: position.x,
      originY: position.y,
    };
  };

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth <= MOBILE_HUD_BREAKPOINT;
      setIsMobile(nextIsMobile);
      if (nextIsMobile) {
        setPosition((prev) => clampPosition(prev.x, prev.y));
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const onPointerMove = (event) => {
      const state = dragStateRef.current;
      if (!state.active) return;
      const pointerX = event.clientX ?? event.touches?.[0]?.clientX ?? 0;
      const pointerY = event.clientY ?? event.touches?.[0]?.clientY ?? 0;
      const deltaX = pointerX - state.startX;
      const deltaY = pointerY - state.startY;
      const next = clampPosition(state.originX + deltaX, state.originY + deltaY);
      setPosition(next);
    };

    const onPointerUp = () => {
      if (!dragStateRef.current.active) return;
      dragStateRef.current.active = false;
      persistState(position);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [isMobile, position]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      writeHudState(storageKey, {
        x: position.x,
        y: position.y,
        collapsed: next,
      });
      return next;
    });
  };

  useEffect(() => {
    if (!isMobile) return;
    const clamped = clampPosition(position.x, position.y);
    if (clamped.x !== position.x || clamped.y !== position.y) {
      setPosition(clamped);
      return;
    }
    writeHudState(storageKey, {
      x: clamped.x,
      y: clamped.y,
      collapsed,
    });
  }, [collapsed, isMobile, position.x, position.y]);

  const containerStyle = isMobile
    ? {
      position: 'fixed',
      left: 0,
      top: 0,
      transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
      width: `min(${panelWidth}px, calc(100vw - 16px))`,
      maxWidth: 'calc(100vw - 16px)',
      zIndex: desktopPosition.zIndex,
      touchAction: 'none',
    }
    : {
      position: 'fixed',
      top: desktopPosition.top,
      left: desktopPosition.left,
      right: desktopPosition.right,
      zIndex: desktopPosition.zIndex,
      width: desktopPosition.width,
      maxWidth: desktopPosition.maxWidth,
    };

  return {
    isMobile,
    collapsed,
    toggleCollapsed,
    onDragStart,
    panelRef,
    containerStyle,
  };
}

function SessionLockedRoute({ children }) {
  const { activeSessionContext } = useSessionComms();
  if (activeSessionContext?.code) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function SessionUnloadGuard() {
  const { activeSessionContext } = useSessionComms();
  const isLocked = Boolean(activeSessionContext?.code);

  useEffect(() => {
    if (!isLocked) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isLocked]);

  return null;
}

function SessionGameSyncController() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeSessionContext, session } = useSessionComms();

  const activeGame = session?.activeVirtualGame || null;
  const activeGameRoute = String(activeGame?.route || '').trim();
  const activeGameMarker = String(activeGame?.startedAt || '');
  const hasActiveSession = Boolean(activeSessionContext?.code);
  const activeGameEnabled = Boolean(activeGame?.active && SESSION_FORCED_GAME_ROUTES.has(activeGameRoute));

  useEffect(() => {
    if (!hasActiveSession) return;

    if (activeGameEnabled) {
      if (location.pathname !== activeGameRoute) {
        navigate(activeGameRoute, {
          replace: true,
          state: {
            ...(activeGame?.setup || {}),
            __sessionGameSnapshot: activeGame?.snapshot ?? null,
            __sessionGameName: activeGame?.game || null,
          },
        });
      }
      return;
    }

    if (SESSION_FORCED_GAME_ROUTES.has(location.pathname)) {
      navigate('/', { replace: true });
    }
  }, [
    hasActiveSession,
    activeGameEnabled,
    activeGameRoute,
    activeGameMarker,
    location.pathname,
    navigate,
    activeGame?.setup,
  ]);

  return null;
}

function GlobalSessionActivityWidget() {
  const { theme } = useTheme();
  const {
    activeSessionContext,
    voiceEnabled,
    localMuted,
    localSpeaking,
    activeSpeakers,
    remoteVoices,
  } = useSessionComms();

  const hudPanel = useMobileHudPanel({
    storageKey: 'gn_mobile_hud_live_session',
    desktopPosition: {
      top: '16px',
      left: undefined,
      right: '16px',
      zIndex: 1200,
      width: '220px',
      maxWidth: 'calc(100vw - 32px)',
    },
    mobileDefaultPosition: () => ({ x: 8, y: 82 }),
    panelWidth: 240,
    panelHeight: 120,
    defaultCollapsed: false,
  });

  if (!activeSessionContext?.code) {
    return null;
  }

  const remoteSpeakerRows = remoteVoices
    .map((voice) => ({
      alias: voice.alias,
      speaking: Boolean(activeSpeakers[voice.alias]),
    }))
    .slice(0, 6);

  return (
    <div
      ref={hudPanel.panelRef}
      style={{
        ...hudPanel.containerStyle,
        backgroundColor: theme.bgDark,
        border: `1px solid ${theme.secondary}`,
        borderRadius: '10px',
        boxShadow: '0 10px 24px rgba(0,0,0,0.3)',
        padding: '10px',
        display: 'grid',
        gap: '6px',
        pointerEvents: hudPanel.isMobile ? 'auto' : 'none'
      }}
    >
      {hudPanel.isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <div
            onPointerDown={hudPanel.onDragStart}
            style={{
              cursor: 'grab',
              fontSize: '0.75rem',
              color: theme.textSecondary,
              border: `1px dashed ${theme.secondary}`,
              borderRadius: '6px',
              padding: '3px 6px',
              userSelect: 'none'
            }}
          >
            Move
          </div>
          <button
            onClick={hudPanel.toggleCollapsed}
            style={{
              border: `1px solid ${theme.secondary}`,
              backgroundColor: 'transparent',
              color: theme.textPrimary,
              borderRadius: '6px',
              padding: '4px 8px',
              fontFamily: 'inherit',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            {hudPanel.collapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>
      )}

      <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>Live Session</div>
      <div style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{activeSessionContext.code}</div>

      {(!hudPanel.isMobile || !hudPanel.collapsed) && (
        <>
          <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>
            Voice: {voiceEnabled ? (localMuted ? 'Connected (Muted)' : 'Connected') : 'Off'}
          </div>

          {voiceEnabled && (
            <div style={{ display: 'grid', gap: '4px' }}>
              <div style={{
                fontSize: '0.8rem',
                color: localSpeaking && !localMuted ? theme.success : theme.textSecondary,
                fontWeight: 'bold'
              }}>
                You: {localSpeaking && !localMuted ? 'Speaking' : 'Quiet'}
              </div>
              {remoteSpeakerRows.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>No remote voices</div>
              ) : (
                remoteSpeakerRows.map((row) => (
                  <div
                    key={`speaker-${row.alias}`}
                    style={{
                      fontSize: '0.78rem',
                      color: row.speaking ? theme.success : theme.textSecondary,
                      fontWeight: row.speaking ? 'bold' : 'normal'
                    }}
                  >
                    {row.alias}: {row.speaking ? 'Speaking' : 'Quiet'}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GlobalSessionLockBar() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const [endingGame, setEndingGame] = useState(false);
  const {
    activeSessionContext,
    session,
    clearSessionContext,
    emitRealtimeEvent,
  } = useSessionComms();
  const hudPanel = useMobileHudPanel({
    storageKey: 'gn_mobile_hud_lock_bar',
    desktopPosition: {
      top: '12px',
      left: '12px',
      right: undefined,
      zIndex: 1300,
      width: undefined,
      maxWidth: 'calc(100vw - 24px)',
    },
    mobileDefaultPosition: () => ({ x: 8, y: 8 }),
    panelWidth: 360,
    panelHeight: 80,
    defaultCollapsed: false,
  });

  if (!activeSessionContext?.code) {
    return null;
  }

  const code = String(activeSessionContext.code || '').trim().toUpperCase();
  const alias = String(activeSessionContext.alias || '').trim();
  const resolvedHostAlias = String(session?.hostAlias || alias).trim();
  const role = activeSessionContext.role === 'host' ? 'host' : 'participant';
  const activeGame = session?.activeVirtualGame || null;
  const hasActiveGame = Boolean(activeGame?.active && SESSION_FORCED_GAME_ROUTES.has(String(activeGame?.route || '').trim()));

  const clearLocalSessionState = () => {
    storageManager.removeTemporaryData(`guest_session_presence_${code}`);
    storageManager.removeTemporaryData(HOST_SESSION_CACHE_KEY);
    clearSessionContext(true);
  };

  const handleLeave = async () => {
    if (!code || leaving) return;
    try {
      setLeaving(true);
      if (role === 'host') {
        try {
          await sessionApi.endGuestSession(code, { hostAlias: resolvedHostAlias || undefined });
          emitRealtimeEvent('session-updated', { code, action: 'ended', alias: resolvedHostAlias || alias }, code).catch(() => null);
          emitRealtimeEvent('messages-updated', { code, action: 'ended', alias: resolvedHostAlias || alias }, code).catch(() => null);
        } catch {
          // If host ending fails (stale role/context), fall back to a normal leave.
          if (alias) {
            await sessionApi.leaveGuestSession(code, { alias });
            emitRealtimeEvent('session-updated', { code, action: 'left', alias }, code).catch(() => null);
            emitRealtimeEvent('messages-updated', { code, action: 'left', alias }, code).catch(() => null);
          }
        }
      } else {
        if (alias) {
          await sessionApi.leaveGuestSession(code, { alias });
          emitRealtimeEvent('session-updated', { code, action: 'left', alias }, code).catch(() => null);
          emitRealtimeEvent('messages-updated', { code, action: 'left', alias }, code).catch(() => null);
        }
      }
    } catch {
      // Fallback to local cleanup so users are never stuck in locked mode.
    } finally {
      clearLocalSessionState();
      setLeaving(false);
      navigate('/');
    }
  };

  const handleEndGame = async () => {
    if (!code || role !== 'host' || !hasActiveGame || endingGame) return;
    try {
      setEndingGame(true);
      await sessionApi.endVirtualGame(code, { endedByAlias: resolvedHostAlias || alias || undefined });
      emitRealtimeEvent(
        'session-updated',
        { code, action: 'virtual-game-ended', game: activeGame?.game || null },
        code
      ).catch(() => null);
      emitRealtimeEvent(
        'messages-updated',
        { code, action: 'virtual-game-ended', game: activeGame?.game || null },
        code
      ).catch(() => null);
    } catch {
      // no-op: periodic session sync will still reconcile if backend already ended game
    } finally {
      setEndingGame(false);
    }
  };

  return (
    <div
      ref={hudPanel.panelRef}
      style={{
        ...hudPanel.containerStyle,
        backgroundColor: theme.bgDark,
        border: `1px solid ${theme.secondary}`,
        borderRadius: '10px',
        boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
        padding: '8px 10px',
        display: 'grid',
        gap: '8px'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        {hudPanel.isMobile ? (
          <div
            onPointerDown={hudPanel.onDragStart}
            style={{
              cursor: 'grab',
              fontSize: '0.75rem',
              color: theme.textSecondary,
              border: `1px dashed ${theme.secondary}`,
              borderRadius: '6px',
              padding: '3px 6px',
              userSelect: 'none'
            }}
          >
            Move
          </div>
        ) : (
          <span style={{ fontSize: '0.82rem', color: theme.textSecondary }}>
            Session Locked:
          </span>
        )}
        <strong style={{ fontSize: '0.95rem' }}>{code}</strong>
        {hudPanel.isMobile && (
          <button
            onClick={hudPanel.toggleCollapsed}
            style={{
              border: `1px solid ${theme.secondary}`,
              backgroundColor: 'transparent',
              color: theme.textPrimary,
              borderRadius: '6px',
              padding: '4px 8px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}
          >
            {hudPanel.collapsed ? 'Expand' : 'Collapse'}
          </button>
        )}
      </div>

      {(!hudPanel.isMobile || !hudPanel.collapsed) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {hudPanel.isMobile && (
            <span style={{ fontSize: '0.82rem', color: theme.textSecondary }}>
              Session Locked:
            </span>
          )}
          <button
            onClick={() => navigate(`/session/${code}`)}
            style={{
              border: `1px solid ${theme.secondary}`,
              backgroundColor: 'transparent',
              color: theme.textPrimary,
              borderRadius: '6px',
              padding: '6px 8px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}
          >
            Open Session
          </button>
          {role === 'host' && hasActiveGame && (
            <button
              onClick={handleEndGame}
              disabled={endingGame}
              style={{
                border: 'none',
                backgroundColor: theme.warning || theme.accent,
                color: theme.textPrimary,
                borderRadius: '6px',
                padding: '6px 10px',
                fontFamily: 'inherit',
                cursor: endingGame ? 'not-allowed' : 'pointer',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                opacity: endingGame ? 0.7 : 1
              }}
            >
              {endingGame ? 'Ending Game...' : 'End Game'}
            </button>
          )}
          <button
            onClick={handleLeave}
            disabled={leaving}
            style={{
              border: 'none',
              backgroundColor: theme.danger,
              color: theme.textPrimary,
              borderRadius: '6px',
              padding: '6px 10px',
              fontFamily: 'inherit',
              cursor: leaving ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              opacity: leaving ? 0.7 : 1
            }}
          >
            {leaving ? 'Leaving...' : (role === 'host' ? 'End Session' : 'Leave Session')}
          </button>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SessionCommsProvider>
          <div className="gn-app-shell">
            <div className="gn-global-pattern-overlay" aria-hidden="true" />
            <Router>
              <div className="gn-content-layer">
                <SessionUnloadGuard />
                <SessionGameSyncController />
                <AccountGameStateSync />
                <GlobalSessionLockBar />
                <GlobalSessionActivityWidget />
                <div className="gn-route-layer">
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/session/:code" element={<GuestSessionPage />} />

                    {/* Imposter Game Routes */}
                    <Route path="/imposter-setup" element={<SessionLockedRoute><ImposterSetup /></SessionLockedRoute>} />
                    <Route path="/imposter-virtual" element={<VirtualImposter />} />
                    <Route path="/imposter-reveal" element={<SessionLockedRoute><ImposterReveal /></SessionLockedRoute>} />
                    <Route path="/imposter-voting" element={<SessionLockedRoute><ImposterVoting /></SessionLockedRoute>} />

                    {/* UNO Routes */}
                    <Route path="/uno-test" element={<UnoTest />} />
                    <Route path="/uno-logic-test" element={<UnoLogicTest />} />
                    <Route path="/uno-setup" element={<UnoSetup />} />
                    <Route path="/uno-game" element={<UnoGame />} />

                    {/* UNO No Mercy Routes */}
                    <Route path="/uno-nm-setup" element={<UnoNMSetup />} />
                    <Route path="/uno-nm-game" element={<UnoNMGame />} />

                    {/* Mafia Routes */}
                    <Route path="/mafia-setup" element={<SessionLockedRoute><MafiaSetup /></SessionLockedRoute>} />
                    <Route path="/mafia-game" element={<SessionLockedRoute><MafiaGame /></SessionLockedRoute>} />
                    <Route path="/mafia-reveal" element={<SessionLockedRoute><MafiaReveal /></SessionLockedRoute>} />
                    <Route path="/mafia-results" element={<SessionLockedRoute><MafiaResults /></SessionLockedRoute>} />
                    <Route path="/mafia-narrator" element={<SessionLockedRoute><MafiaNarrator /></SessionLockedRoute>} />

                    {/* Charades Routes */}
                    <Route path="/charades-setup" element={<SessionLockedRoute><CharadesSetup /></SessionLockedRoute>} />
                    <Route path="/charades-game" element={<SessionLockedRoute><CharadesGame /></SessionLockedRoute>} />

                    {/* Trio Routes */}
                    <Route path="/trio-setup" element={<TrioSetup />} />
                    <Route path="/trio-game" element={<TrioGame />} />
                  </Routes>
                </div>
              </div>
            </Router>
          </div>
        </SessionCommsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
